import React, { useState, useEffect } from 'react';
import './dynamicForm.css';
import { Link } from 'react-router-dom';

export default function DynamicForm({ schema, mode = 'add', initialData = {}, onAction, formStyle = {}, buttonHolderStyle = {}, actionButtonStyle = {} }) {
    const { fields, buttons } = schema;

    // build default state from schema
    const buildDefaultData = () =>
        fields.reduce((acc, field) => {
            if (field.type === 'group') {
                field.children.forEach(child => {
                    acc[child.name] = child.defaultValue || '';
                });
            } else {
                acc[field.name] =
                    field.defaultValue != null
                        ? field.defaultValue
                        : field.type === 'checkbox'
                            ? false
                            : '';
            }
            return acc;
        }, {});

    // initial form data: merge buildDefaultData with initialData in edit mode
    const [formData, setFormData] = useState(() =>
        mode === 'edit'
            ? { ...buildDefaultData(), ...initialData }
            : buildDefaultData()
    );

    // update when initialData changes (edit mode)
    useEffect(() => {
        if (mode === 'edit') {
            setFormData({ ...buildDefaultData(), ...initialData });
        }
    }, [initialData, mode, fields]);

    const [showPassword, setShowPassword] = useState({});

    const handleChange = e => {
        const { name, type, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleButton = action => {
        onAction?.(action, formData);
    };

    return (
        <form
            className="dynamic-form"
            onSubmit={e => {
                e.preventDefault();
            }}
            style={{ ...formStyle }}
        >
            {fields.map(field => {
                switch (field.type) {
                    case 'text':
                    case 'email':
                    case 'tel':
                        return (
                            <div className="form-group" key={field.name}>
                                <label className="form-label" htmlFor={field.name}>
                                    {field.label}
                                    {field.required && <span className="required-dot">*</span>}
                                </label>
                                <input
                                    className="form-input"
                                    id={field.name}
                                    name={field.name}
                                    type={field.type}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                    value={formData[field.name]}
                                    onChange={handleChange}
                                />
                            </div>
                        );

                    case 'password':
                        return (
                            <div className="form-group password-group" key={field.name}>
                                <label className="form-label" htmlFor={field.name}>
                                    {field.label}
                                    {field.required && <span className="required-dot">*</span>}
                                </label>
                                <input
                                    className="form-input"
                                    id={field.name}
                                    name={field.name}
                                    type={showPassword[field.name] ? 'text' : 'password'}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                    value={formData[field.name]}
                                    onChange={handleChange}
                                />
                                <span
                                    className="toggle-password"
                                    onClick={() =>
                                        setShowPassword(prev => ({
                                            ...prev,
                                            [field.name]: !prev[field.name],
                                        }))
                                    }
                                >
                                    {showPassword[field.name] ?
                                        <span class="material-symbols-outlined">visibility</span> :
                                        <span class="material-symbols-outlined">visibility_off</span>}
                                </span>
                            </div>
                        );

                    case 'select':
                        return (
                            <div className="form-group" key={field.name}>
                                <label className="form-label" htmlFor={field.name}>
                                    {field.label}
                                    {field.required && <span className="required-dot">*</span>}
                                </label>
                                <select
                                    className="form-input"
                                    id={field.name}
                                    name={field.name}
                                    required={field.required}
                                    value={formData[field.name]}
                                    onChange={handleChange}
                                >
                                    <option value="">{field.placeholder || 'Select…'}</option>
                                    {field.options.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        );

                    case 'group':
                        return (
                            <div className="flex-row" key={field.name}>
                                {field.children.map(child => (
                                    <div className="form-group" key={child.name} style={{ flex: 1 }}>
                                        <label className="form-label" htmlFor={child.name}>
                                            {child.label}
                                            {child.required && <span className="required-dot">*</span>}
                                        </label>

                                        {child.type === 'select' ? (
                                            <select
                                                className="form-input"
                                                id={child.name}
                                                name={child.name}
                                                required={child.required}
                                                value={formData[child.name]}
                                                onChange={handleChange}
                                            >
                                                <option value="">{child.placeholder || 'Select…'}</option>
                                                {child.options.map(opt => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                className="form-input"
                                                id={child.name}
                                                name={child.name}
                                                type={child.type}
                                                placeholder={child.placeholder}
                                                required={child.required}
                                                value={formData[child.name]}
                                                onChange={handleChange}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        );

                    case 'prefixInput':
                        return (
                            <div className="form-group" key={field.name}>
                                <label className="form-label" htmlFor={field.name}>
                                    {field.label}
                                    {field.required && <span className="required-dot">*</span>}
                                </label>

                                <div className="input-wrapper">
                                    {/* country‐code dropdown */}
                                    <select
                                        className="form-input"
                                        name={`${field.name}Prefix`}
                                        value={formData[`${field.name}Prefix`] || ''}
                                        onChange={handleChange}
                                        required={field.required}
                                    >
                                        <option value="">{field.placeholder || 'Select…'}</option>
                                        {field.options.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>

                                    {/* the phone‐number input */}
                                    <input
                                        className="form-input"
                                        id={field.name}
                                        name={field.name}
                                        type="tel"
                                        placeholder={field.placeholder}
                                        required={field.required}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        );

                    case 'captcha':
                        return (
                            <div className="form-group captcha-group" key={field.name}>
                                <label className="form-label" htmlFor={field.name}>
                                    {field.label}
                                    {field.required && <span className="required-dot">*</span>}
                                </label>

                                <div className="captcha-wrapper">
                                    {/* the image itself */}
                                    <img
                                        className="captcha-image"
                                        src={field.src}
                                        alt={field.alt || 'Captcha'}
                                        onClick={() => {
                                            /* you could also trigger a reload: e.g. call onAction('reloadCaptcha') */
                                            // force-refresh by adding a timestamp:
                                            const stamp = Date.now();
                                            document
                                                .querySelector('.captcha-image')
                                                .setAttribute('src', `${field.src}?t=${stamp}`);
                                        }}
                                    />

                                    {/* the text input */}
                                    <input
                                        className="form-input"
                                        id={field.name}
                                        name={field.name}
                                        type="text"
                                        placeholder=""
                                        required={field.required}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        );

                    case 'terms':
                        return (
                            <div className="form-group terms-group" key={field.name}>
                                <input
                                    className="terms-checkbox"
                                    type="checkbox"
                                    id={field.name}
                                    name={field.name}
                                    checked={formData[field.name]}
                                    onChange={handleChange}
                                    required={field.required}
                                />
                                <label htmlFor={field.name} className="form-label terms-label">
                                    {field.label}
                                    <a href={field.href} target="_blank" rel="noopener noreferrer">{field.linkText}</a>
                                </label>
                            </div>
                        );

                    case 'forgotPassword':
                        return (
                            <div className="form-group terms-group" key={field.name}>
                                <Link to="/forgot-password" className="forgot-password-link">
                                    <label htmlFor={field.name} className="form-label forgot-password-label">
                                        {field.label}
                                    </label>
                                </Link>
                            </div>
                        );

                    case 'resendForgotPasswordLink':
                        return (
                            <div className="form-group resend-link" key={field.name}>
                                <label className="resend-link-label1">
                                    {field.line1}
                                </label>
                                <label onClick={() => (field.action2) ? handleButton(field.action2) : null} className="form-label forgot-password-label">
                                    {field.line2}
                                </label>
                            </div>
                        );

                    case 'group_radio':
                        return (
                            <fieldset className="form-group-radio group-radio" key={field.name}>
                                <legend className="form-label">
                                    {field.label}{field.required && <span className="required-dot">*</span>}
                                </legend>
                                <div className="group-radio-wrapper-list">
                                    {field.children.map(child => (
                                        <div className="radio-card" key={child.name}>
                                            <label className="radio-card-label">{child.label}</label>
                                            <div className="radio-group">
                                                {child.options.map(opt => (
                                                    <label key={opt.value} className="radio-option">
                                                        <input
                                                            type="radio"
                                                            name={child.name}
                                                            value={opt.value}
                                                            checked={formData[child.name] === opt.value}
                                                            onChange={handleChange}
                                                        />
                                                        {opt.label}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </fieldset>
                        );
                    default:
                        return null;
                }




            })}

            <div className="form-actions" style={{ ...buttonHolderStyle }}>
                {buttons.map(btn => (
                    <button
                        key={btn.action}
                        type={btn.type === 'submit' ? 'submit' : 'button'}
                        className={btn.className}
                        onClick={() => handleButton(btn.action)}
                        style={{ ...actionButtonStyle }}
                    >
                        {btn.label}
                    </button>
                ))}
            </div>
        </form>
    );
}
